import {
  EcommerceAPIApi as EcommerceApi,
  type PermitCalculateRequest,
  type PermitCalculateResponse,
  type GarageSearchRequest as PkgGarageSearchRequest,
  type CouponValidateRequest as PkgCouponValidateRequest,
} from '@parkingbase/ecommerce-api-demo';
import { BaseApiService } from './baseApi.service';
import type {
  GarageSearchRequest,
  GarageSearchResponse,
  CouponValidateRequest,
  CouponValidateResponse,
  EventOccurrenceResult,
} from '@/types/reserve.types';
import { executeRecaptcha, makeRecaptchaHeader } from '@/lib/recaptcha';

export class EcommerceSearchService extends BaseApiService {
  protected ecommerceApi: EcommerceApi;

  constructor() {
    super();
    this.ecommerceApi = new EcommerceApi(this.apiConfig, undefined, this.apiClient);
  }

  /**
   * The garage/product search.
   *
   * ## Invariants:
   * - **A non-blank `couponCode` makes a reCAPTCHA token MANDATORY (PB-5578).** An ordinary
   *   search requires none and still sends none; a coupon-bearing search without one is rejected
   *   outright by the server, so the whole feature fails closed if this is missed. The gate keys
   *   on the code the request actually carries, never on a caller-supplied flag — a caller that
   *   forgets the flag would otherwise send a coupon with no token and get a rejection that looks
   *   like a bad code.
   * - **The token is obtained here, not by the caller.** Every call site would otherwise have to
   *   remember the rule, and the one that forgot would fail only for customers who type a code.
   * - **An empty token is treated as a failure, not as a token.** `executeRecaptcha` does not
   *   throw when the site key is unconfigured — it warns and returns `''`, and
   *   `makeRecaptchaHeader('')` then yields `undefined`, so without this guard the coupon
   *   request would go out with no header at all and be rejected by the server. The customer
   *   would see the generic transport error on every attempt, in any environment where the key
   *   is unset, indistinguishable from an outage and with nothing naming the real cause. The
   *   throw makes it attributable in the logs.
   * - Blank-but-present is treated as no code, matching the server: `'   '` skips the coupon path
   *   entirely rather than burning a captcha execution on a request that will not use it.
   * - The cast to the package request type is the coupon bridge. `couponCode` and the response's
   *   coupon fields live on the LOCAL DTOs because the installed client predates the api change —
   *   see the shared removal condition in `reserve.types.ts`. Nothing else is needed; when the
   *   client catches up, the cast goes and the local fields stay.
   */
  async searchGarages(request: GarageSearchRequest): Promise<GarageSearchResponse> {
    const hasCoupon = (request.couponCode ?? '').trim() !== '';

    // The two calls are deliberately NOT collapsed into one with optional arguments. The
    // ordinary path must invoke the client with the EXACT argument list it always has —
    // `searchGarages(request)` and nothing more. Threading `undefined, undefined` through it
    // would be behaviourally identical and would still break every existing assertion on the
    // call shape, which is the legacy lock for the path every current caller takes. Note the
    // options are the THIRD positional parameter; the second is `garageCompany`, so a config
    // passed in slot 2 would be sent as the company.
    const response = hasCoupon
      ? await this.ecommerceApi.searchGarages(request as PkgGarageSearchRequest, undefined, {
          headers: makeRecaptchaHeader(await this.couponCaptchaToken()),
        })
      : await this.ecommerceApi.searchGarages(request as PkgGarageSearchRequest);

    return response.data as GarageSearchResponse;
  }

  /**
   * The reCAPTCHA token for a coupon-bearing search, or a throw.
   *
   * ## Invariants:
   * - Never returns an empty string. That is what `executeRecaptcha` yields when the site key is
   *   unconfigured, and an empty token produces no header at all rather than a rejected one — so
   *   the failure would surface as a server rejection of a tokenless request instead of as the
   *   configuration problem it is.
   */
  private async couponCaptchaToken(): Promise<string> {
    const token = await executeRecaptcha('garage_search_coupon');
    if (!token) {
      throw new Error('reCAPTCHA unavailable for a coupon-bearing search');
    }
    return token;
  }

  async validateCoupon(
    request: CouponValidateRequest,
    recaptchaToken: string
  ): Promise<CouponValidateResponse> {
    const response = await this.ecommerceApi.validateCoupon(
      request as unknown as PkgCouponValidateRequest,
      { headers: { ...makeRecaptchaHeader(recaptchaToken) } }
    );
    return response.data as CouponValidateResponse;
  }

  async calculatePermit(request: PermitCalculateRequest): Promise<PermitCalculateResponse> {
    const response = await this.ecommerceApi.calculatePermit(request);
    return response.data;
  }

  async getEventOccurrences(
    garageId: string,
    startDateTime: string,
    endDateTime: string,
    recaptchaToken: string
  ): Promise<EventOccurrenceResult[]> {
    const response = await this.ecommerceApi.getEventOccurrences(
      garageId,
      startDateTime,
      endDateTime,
      { headers: { ...makeRecaptchaHeader(recaptchaToken) } }
    );
    return (response.data ?? []) as EventOccurrenceResult[];
  }
}
